import { Component, Inject, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { FilterCriteriaService, FilterCriterionModel, FilterGroupModel, FilterGroupsService, Language, UpdateTranslationOverride } from "../../api";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { HttpLoaderService, ListOptions, SearchDataRequest, ValidationMessage } from "@tymes4-shared";
import { KeyValuePair } from "../../models/key-value-pair";
import { GenericErrorHandler } from "../../helpers/generic-error-handler";
import { ConfirmFilterDeletionComponent } from "../confirm-filter-deletion/confirm-filter-deletion.component";
import { InputTranslationsComponent } from "../../components/input-translations/input-translations.component";
import { TranslationHelperService } from "../../services/translation-helper.service";
import { TranslateService } from "@ngx-translate/core";

const integerValidators = [Validators.required, Validators.pattern("-?[0-9]+"), Validators.min(-2147483648), Validators.max(2147483647)];

@Component({
    selector: 'app-edit-filter-group-form',
    templateUrl: './edit-filter-group-form.component.html',
    styleUrls: ['./edit-filter-group-form.component.scss'],
    standalone: false
})
export class EditFilterGroupComponent implements OnInit {

  @ViewChild('criteriaListVw') criteriaListVw: any; // can't use ListViewComponent type here because of circular dependency, but we only need the Data and refreshData properties/methods
  @ViewChild('groupNameInput') groupNameInput?: InputTranslationsComponent;
  @ViewChildren('criterionNameInput') criterionNameInputs?: QueryList<InputTranslationsComponent>;

  public form!: FormGroup;
  public criteria: FilterCriterionModel[] = [];
  public showIdColumn = false;
  public saving = false;

  public translationLanguages: Language[] = [];
  public translationsWithLanguageCode: Record<string, Record<string, string>> = {};
  public translationsLoaded = false;

  private nextFakeId = -1;

  public validationMessages: KeyValuePair<Array<ValidationMessage>> = {
    DisplayOrdinalNumber: [
      { type: 'pattern', message: 'LOCATIONS.FILTER-GROUPS.DIALOG.FORM.VALIDATION.DISPLAY-ORDINAL-NUMBER.INTEGER' },
    ]
  };

  public criteriaListOptions: ListOptions = {
    search: false,
    pageSize: 6,
    renderInCard: false,
    noItemsText: 'LOCATIONS.FILTER-GROUPS.DIALOG.CRITERIA-LISTVIEW.NO-ITEMS',
    headers: [
      { sortField: 'DisplayOrdinalNumber', label: 'LOCATIONS.FILTER-GROUPS.DIALOG.CRITERIA-LISTVIEW.HEADER.DISPLAY-ORDINAL-NUMBER', width: 110, isDefaultSort: 'asc' },
      { sortField: 'Code', label: 'LOCATIONS.FILTER-GROUPS.DIALOG.CRITERIA-LISTVIEW.HEADER.FRIENDLY-NAME', width: null },
      { label: '' }
    ]
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public passedData: { title: string, item: FilterGroupModel },
    public dialogRef: MatDialogRef<EditFilterGroupComponent>,
    private filterGroupsService: FilterGroupsService,
    private filterCriteriaService: FilterCriteriaService,
    private matDialog: MatDialog,
    private errorHandler: GenericErrorHandler,
    private loader: HttpLoaderService,
    private translationHelperService: TranslationHelperService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.form = new FormGroup({
      Id: new FormControl(0),
      Code: new FormControl(''),
      DisplayOrdinalNumber: new FormControl('', integerValidators)
    });

    this.form.patchValue(this.passedData.item);

    if (Array.isArray(this.passedData.item?.Criteria)) {
      this.criteria = [...this.passedData.item.Criteria];

      this.showIdColumn = this.criteria.some(c => c.Id > 0);
      if (this.showIdColumn) {
        this.criteriaListOptions.headers.unshift({ sortField: 'Id', label: 'LOCATIONS.FILTER-GROUPS.DIALOG.CRITERIA-LISTVIEW.HEADER.ID', width: 40 });
      }

      for (const criterion of this.criteria) {
        this.addCriterionToForm(criterion);
      }
    }

    this.translationHelperService.load().subscribe(cache => {
      this.translationLanguages = cache.languages;
      this.translationsWithLanguageCode = cache.translationsByLanguageCode;
      this.translationsLoaded = true;
    });
  }

  public getCriterionFormGroupName(criterion: FilterCriterionModel) {
    return "Criterion" + criterion.Id;
  }

  private getCriterionControl(criterion: FilterCriterionModel, property: keyof FilterCriterionModel) {
    const formGroupName = this.getCriterionFormGroupName(criterion);
    return this.form.get(formGroupName)?.get(property);
  }

  private getCriterionProperty(criterion: FilterCriterionModel, property: keyof FilterCriterionModel) {
    return this.getCriterionControl(criterion, property)?.value;
  }

  private addCriterionToForm(criterion: FilterCriterionModel) {
    const name = this.getCriterionFormGroupName(criterion);
    this.form.addControl(name, new FormGroup({
      Id: new FormControl(criterion.Id),
      Code: new FormControl(criterion.Code ?? ''),
      DisplayOrdinalNumber: new FormControl(criterion.DisplayOrdinalNumber, integerValidators)
    }));
  }
  
  private removeCriterionFromForm(criterion: FilterCriterionModel) {
    const name = this.getCriterionFormGroupName(criterion);
    this.form.removeControl(name);
  }

  public addCriterion() {
    const newCriterion = {
      Id: this.nextFakeId--,
      FilterGroupId: this.passedData.item?.Id ?? 0,
      Code: '',
      DisplayOrdinalNumber: this.criteria.length > 0 ? Math.max(...this.criteria.map(c => c.DisplayOrdinalNumber)) + 1 : 1
    } as FilterCriterionModel;

    this.addCriterionToForm(newCriterion);
    this.criteria.push(newCriterion);

    this.criteriaListVw.refreshData();
    this.form.markAsDirty();
  }

  public onCriteriaDataRequest(request: SearchDataRequest) {
    const filteredCriteria = request.searchText
      ? this.criteria.filter(criterion =>
          this.getCriterionProperty(criterion, 'Code')?.toLowerCase().includes(request.searchText.toLowerCase()))
      : this.criteria;

    if (request.sortField) {
      const sortField = request.sortField as keyof FilterCriterionModel;
      filteredCriteria.sort((criterion1, criterion2) => compare(this.getCriterionProperty(criterion1, sortField), this.getCriterionProperty(criterion2, sortField), request.sortAsc));
    }

    this.criteriaListVw.Data = {
      TotalRecords: this.criteria.length,
      Records: filteredCriteria.slice((request.pageNr - 1) * request.pageSize, request.pageNr * request.pageSize)
    };
  }

  public tryDeleteCriterion(criterion: FilterCriterionModel) {
    if (criterion.Id <= 0) {
      this.deleteCriterion(criterion);
      return;
    }

    this.loader.open();
    this.filterCriteriaService.getFilterCriterionUsages(criterion.Id).subscribe({
      next: usages => {
        this.loader.close();

        if (usages.length == 0) {
          this.deleteCriterion(criterion);
          return;
        }

        const options = {
          disableClose: true,
          panelClass: 'confirmBox',
          data: {
            title: 'LOCATIONS.FILTER-GROUPS.CONFIRM-DELETION.TITLE.FILTER-CRITERION',
            friendlyName: this.translateService.instant(criterion.Code ?? ''),
            usages: usages
          }
        };
        const dialogRef: MatDialogRef<any> = this.matDialog.open(ConfirmFilterDeletionComponent, options);
        dialogRef.afterClosed().subscribe(confirmed => {
          if (confirmed) {
            this.deleteCriterion(criterion);
          }
        });
      },
      error: err => {
        this.errorHandler.handle(err);
      }
    });
  }

  private deleteCriterion(criterion: FilterCriterionModel) {
    const index = this.criteria.findIndex(c => c.Id === criterion.Id);
    if (index == -1) {
      console.warn("Unknown criterion.", criterion);
      return;
    }

    this.criteria.splice(index, 1);
    this.removeCriterionFromForm(criterion);

    this.form.markAsDirty();
    this.criteriaListVw.refreshData();
  }

  public isDisplayOrdinalNumberInvalid(criterion: FilterCriterionModel) {
    const control = this.getCriterionControl(criterion, 'DisplayOrdinalNumber');
    return control?.dirty && control?.invalid;
  }

  public onTranslationTouched(event: boolean) {
    if (event) {
      this.form.markAsDirty();
    }
  }

  public canSave() {
    return !!this.form && this.form.dirty && this.form.valid && !this.saving && this.hasValidNameInputs();
  }

  private hasValidNameInputs() {
    if (!this.hasAnyTranslationValue(this.groupNameInput?.valuesOfInputs)) {
      return false;
    }

    for (const criterion of this.criteria) {
      const input = this.getCriterionInput(criterion);
      if (!this.hasAnyTranslationValue(input?.valuesOfInputs)) {
        return false;
      }
    }

    return true;
  }

  private getCriterionInput(criterion: FilterCriterionModel) {
    return this.criterionNameInputs?.find(input => input.prefix === this.getCriterionTranslationPrefix(criterion));
  }

  private hasAnyTranslationValue(translation?: UpdateTranslationOverride) {
    if (!translation?.Languages?.length) {
      return false;
    }

    return translation.Languages.some(language => !!language.Override?.trim());
  }

  public getGroupTranslationPrefix() {
    const currentValue = this.form.get('Code')?.value as string;
    if (currentValue?.startsWith('FILTER-GROUP.')) {
      return currentValue;
    }

    return `FILTER-GROUP.TEMP.${this.passedData.item?.Id ?? 'NEW'}`;
  }

  public getCriterionTranslationPrefix(criterion: FilterCriterionModel) {
    const currentValue = this.getCriterionControl(criterion, 'Code')?.value as string;
    if (currentValue?.startsWith('FILTER-CRITERION.')) {
      return currentValue;
    }

    return `FILTER-CRITERION.TEMP.${criterion.Id}`;
  }

  private cloneTranslationOverride(translation: UpdateTranslationOverride, code: string): UpdateTranslationOverride {
    return {
      Code: code,
      SalesChannelId: translation.SalesChannelId,
      Languages: (translation.Languages ?? []).map(language => ({
        LanguageId: language.LanguageId,
        Default: language.Default,
        Override: language.Override
      }))
    };
  }

  public submit() {
    if (!this.hasValidNameInputs()) {
      return;
    }

    this.saving = true;

    const data = this.form.value as FilterGroupModel & { TranslationOverrides: UpdateTranslationOverride[] };
    const translationOverrides: UpdateTranslationOverride[] = [];

    const groupTranslation = this.groupNameInput!.valuesOfInputs;
    const groupKey = groupTranslation.Code ?? this.getGroupTranslationPrefix();

    data.Code = groupKey;
    translationOverrides.push(this.cloneTranslationOverride(groupTranslation, groupKey));

    const criterionInputsByPrefix = new Map(
      (this.criterionNameInputs?.toArray() ?? []).map(input => [input.prefix, input])
    );

    data.Criteria = [];
    for (const criterion of this.criteria) {
      const groupName = this.getCriterionFormGroupName(criterion);
      const criterionData = this.form.get(groupName)?.value as FilterCriterionModel;
      criterionData.FilterGroupId = this.passedData.item?.Id ?? 0;

      const input = criterionInputsByPrefix.get(this.getCriterionTranslationPrefix(criterion));
      if (input?.valuesOfInputs) {
        const criterionKey = input.valuesOfInputs.Code ?? this.getCriterionTranslationPrefix(criterion);
        criterionData.Code = criterionKey;
        translationOverrides.push(this.cloneTranslationOverride(input.valuesOfInputs, criterionKey));
      }

      data.Criteria.push(criterionData);
      data[groupName] = undefined;
    }

    data.TranslationOverrides = translationOverrides;

    const observable = this.passedData.item == null
      ? this.filterGroupsService.createFilterGroup(data)
      : this.filterGroupsService.updateFilterGroup(this.passedData.item.Id, data);

    this.loader.open();
    observable.subscribe({
      next: id => {
        this.loader.close();
        this.dialogRef.close(id ?? data.Id);
      },
      error: (err: any) => {
        this.saving = false;
        this.errorHandler.handle(err);
      }
    });
  }
}

function compare(value1: any, value2: any, sortAsc: boolean) {
  let comparison = typeof value1 === 'string' && typeof value2 === 'string'
    ? value1.localeCompare(value2)
    : (value1 < value2 ? -1 : (value1 > value2 ? 1 : 0));

  if (sortAsc === false) {
    comparison = -comparison;
  }
  return comparison;
}
